/**
 * ✉ 메일로 보내기 버튼 (v2.108.0 · 형식 고르기 흡수 v2.175.0)
 *
 * 문서 화면 어디든 이 버튼 하나로 전역 메일 작성 모달을 연다.
 * viewer 도 v2.250.0 부터 보낸다 — 다만 금액 문서(MAIL_DOC_SALES_ONLY)를 담은 단추는 viewer 에게 그리지 않는다
 *   (서버 doc-source MAIL_DOC_MIN_ROLE 이 막는다 · v2.249.0 까지는 viewer 에게 이 단추가 아예 없었다).
 * compose 는 함수로 받는다 — 클릭 시점의 최신 상태(편집 중인 항목 등)로 초기값을 만들기 위해.
 *
 * ⭐⭐**형식이 둘 이상이면 누를 때 고른다** (견적서 PDF·엑셀 · 기술지원확약서 PDF·워드).
 *   2026-09-01 사용자 지시로 만든 규칙이다 — 예전에는 전부 붙여 놓고 필요 없는 쪽을 ×로 뺐다.
 *   v2.175.0 에서 이 규칙을 **PDF 미리보기 창에서 여기로 옮겼다**: 같은 판정을 두 파일에
 *   적어 두면 반드시 갈린다(한쪽에만 새 형식이 붙는 식으로). 쓰는 곳은 세 군데 —
 *   PDF 미리보기 창 · 견적서 미리보기 창 · 발주서 미리보기 창.
 *
 * ⚠`compose()` 를 **두 번 부른다** — 그릴 때(▾ 를 붙일지 세려고) 한 번, 누를 때(보낼 값)
 *   한 번. 그래서 `compose` 는 **읽기만 하는 순수 함수**여야 한다(부수효과 금지).
 */

import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../lib/auth-context';
import { openMailCompose, type MailComposeInit } from '../lib/mail-compose';
import { MAIL_DOC_SALES_ONLY, type MailDocRef } from '../lib/mail-api';

/** 형식 고르기 메뉴에 쓸 이름 — docType 접미사가 곧 형식이다 (엑셀·워드가 아니면 PDF) */
const FORMAT_LABEL = (docType: string): string =>
  docType.endsWith('-xlsx') ? '📊 엑셀로 보내기'
    : docType.endsWith('-docx') ? '📝 워드로 보내기'
      : '📄 PDF 로 보내기';

export function MailSendButton({
  compose,
  className,
  label,
  title,
  disabled,
  menuDirection = 'down',
  onBeforeOpen,
}: {
  compose: () => MailComposeInit;
  className?: string;
  label?: string;
  title?: string;
  disabled?: boolean;
  /**
   * 형식 고르기 메뉴가 열리는 쪽. 단추가 **창 아래쪽 단추줄**에 있으면 `'up'` —
   * 안 그러면 메뉴가 창 밖으로 나가 잘린다(바깥 상자가 `overflow-y-auto` 라서).
   */
  menuDirection?: 'up' | 'down';
  /** 작성창을 열기 **직전**에 할 일 (예: PDF 미리보기를 닫아 아래 깔리지 않게) */
  onBeforeOpen?: () => void;
}) {
  const { user } = useAuth();
  /** 열려 있는 형식 메뉴가 들고 있는 값 — 누른 그 순간의 compose() 결과 */
  const [menu, setMenu] = useState<MailComposeInit | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const menuOpen = menu !== null;

  /**
   * 바깥을 누르면 메뉴가 닫힌다.
   * ⚠**`fixed inset-0` 덮개를 깔지 않는다** — 그 모양은 `check:modal` 이 「모달 배경 클릭 닫기」로
   *   잡는다(작성 중 입력이 사라지는 사고를 막는 그물). 예외 목록을 늘리는 대신 덮개를 없앴다.
   * ⚠deps 에 객체(menu)를 넣지 말 것 — 매 렌더 새 객체라 효과가 계속 다시 붙는다.
   */
  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e: MouseEvent): void => {
      if (!wrapRef.current?.contains(e.target as Node)) setMenu(null);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [menuOpen]);

  if (!user) return null;

  // ▾ 를 붙일지만 보려고 미리 한 번 세어 본다 (실제로 보낼 값은 누를 때 다시 만든다)
  const preview = compose().docRefs ?? [];
  // 조회 전용(viewer)도 메일을 쓴다(v2.250.0) — 단, 금액이 든 문서(견적서·발주서·거래명세서·비상벨 납기현황)는
  //   영업 권한 이상만 첨부할 수 있어(서버 doc-source MAIL_DOC_MIN_ROLE) 그 단추는 안 그린다. 못 쓰는 단추를 보이지 않는다.
  if (user.role === 'viewer' && preview.some((r) => MAIL_DOC_SALES_ONLY.has(r.docType))) return null;
  const multi = preview.length >= 2;

  const fire = (init: MailComposeInit, refs?: MailDocRef[]): void => {
    setMenu(null);
    onBeforeOpen?.();
    openMailCompose(refs ? { ...init, docRefs: refs } : init);
  };

  return (
    <div className="relative inline-block" ref={wrapRef}>
      <button
        type="button"
        title={title ?? '메일로 보내기'}
        disabled={disabled}
        onClick={(e) => {
          e.stopPropagation();
          const init = compose();
          // 형식이 둘 이상이면 바로 보내지 않고 고르게 한다
          if ((init.docRefs?.length ?? 0) >= 2) {
            setMenu((o) => (o ? null : init));
            return;
          }
          fire(init);
        }}
        className={
          className ??
          'btn btn-sm btn-outline'
        }
      >
        {label ?? '✉ 메일'}{multi ? ' ▾' : ''}
      </button>
      {menu && (
        <div
          className={`absolute right-0 z-[82] w-44 rounded border border-slate-200 bg-white py-1 shadow-lg ${
            menuDirection === 'up' ? 'bottom-full mb-1' : 'top-full mt-1'
          }`}
        >
            {menu.docRefs!.map((ref) => (
              <button
                key={`${ref.docType}:${String(ref.docId)}`}
                type="button"
                onClick={() => fire(menu, [ref])}
                className="block w-full px-3 py-1.5 text-left text-xs text-slate-700 hover:bg-sky-50"
              >
                {FORMAT_LABEL(ref.docType)}
              </button>
            ))}
            <div className="my-1 border-t border-slate-100" />
            <button
              type="button"
              onClick={() => fire(menu, menu.docRefs!)}
              className="block w-full px-3 py-1.5 text-left text-xs text-slate-500 hover:bg-sky-50"
            >
            {menu.docRefs!.length === 2 ? '둘 다 보내기' : '모두 보내기'}
          </button>
        </div>
      )}
    </div>
  );
}
